import { useEffect } from 'react';
import type { OrientationMode } from '@/stores/uiStore';

interface OrientationLockApi {
  lock?: (orientation: string) => Promise<void>;
  unlock?: () => void;
  type?: string;
}

/**
 * Applies a screen-orientation preference using the Screen Orientation API.
 * The browser only honors this in an installed PWA running in standalone
 * mode — in a regular tab the call throws NotSupportedError, which we
 * silently swallow.
 *
 * Mount once at App root with the user's preferred mode from settings.
 */
export function useOrientationLock(mode: OrientationMode) {
  useEffect(() => {
    if (typeof screen === 'undefined' || !screen.orientation) return;
    const api = screen.orientation as unknown as OrientationLockApi;

    if (mode === 'any') {
      try {
        api.unlock?.();
      } catch {
        /* not in PWA — ignore */
      }
      return;
    }

    // 'portrait' or 'landscape' map directly to the spec's primary types.
    api.lock?.(mode).catch(() => {
      /* not in PWA standalone — ignore */
    });
  }, [mode]);
}
